/**
 * Scheduled next-period deliverable changes + the deliverable change log.
 *
 * A CRM user can stage program-deliverable changes (add / remove / quantity) that must NOT touch
 * the current billing period. Each staged entry is stamped with the period's reset date
 * (effectiveDate = current period end at schedule time) and stored on the account:
 *
 *   accounts/{id}.scheduledDeliverableChanges: [{
 *     id, type: 'add' | 'remove' | 'quantity', itemId, itemName,
 *     quantity,            // add: starting qty · quantity: the new qty · remove: unused
 *     previousQuantity,    // qty at schedule time (display / log only)
 *     effectiveDate,       // ISO — the reset date the change applies on
 *     effectivePeriodKey,  // YYYY-MM-DD start of the period it applies to
 *     createdAt, createdBy
 *   }]
 *
 * Application is LAZY: the first time the account's deliverables are computed after the reset
 * date (fetchProgramDeliverablesBundle → applyDueScheduledDeliverableChanges), due entries are
 * folded into the real roster fields (customProgramDeliverables / extraDeliverables /
 * deliverableQuantityOverrides / removedDeliverableIds), removed from the pending list, and
 * recorded in the change log. There is no server cron — this matches the client-trusted pattern
 * used by the rest of the accounts collection.
 *
 * Every deliverable roster edit — immediate or scheduled — is recorded in:
 *
 *   accounts/{id}.deliverableChangeLog: [{ id, timestamp, actor, action, itemId, itemName,
 *     description, effectiveDate? }]
 */
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

const ACCOUNTS_COLLECTION = 'accounts';
const CHANGE_LOG_CAP = 300;

function makeEntryId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Local date parse (ISO string / Firestore Timestamp / millis). Kept here to avoid a module cycle
 *  with accountProgramDeliverables, which imports this service. */
function parseDateish(value) {
  if (value == null || value === '') return null;
  if (typeof value?.toDate === 'function') {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Human description of a scheduled change, used in the modal list and the change log. */
export function describeScheduledChange(change) {
  const name = change?.itemName || change?.itemId || 'deliverable';
  const qty = Number(change?.quantity);
  switch (change?.type) {
    case 'add':
      return `Add ${name}${qty > 0 ? ` (qty ${qty})` : ''}`;
    case 'remove':
      return `Remove ${name}`;
    case 'quantity': {
      const prev = Number(change?.previousQuantity);
      return Number.isFinite(prev)
        ? `${name} quantity ${prev} → ${qty}`
        : `${name} quantity → ${qty}`;
    }
    default:
      return name;
  }
}

export function buildChangeLogEntry({ actor, action, itemId, itemName, description, effectiveDate }) {
  const entry = {
    id: makeEntryId(),
    timestamp: new Date().toISOString(),
    actor: actor || 'Unknown User',
    action,
    itemId: itemId || null,
    itemName: itemName || null,
    description: description || '',
  };
  if (effectiveDate) entry.effectiveDate = effectiveDate;
  return entry;
}

function appendToLog(existingLog, entries) {
  const base = Array.isArray(existingLog) ? existingLog : [];
  const additions = Array.isArray(entries) ? entries : [entries];
  return [...base, ...additions].slice(-CHANGE_LOG_CAP);
}

function requireAuth() {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated to update accounts');
  }
}

/**
 * Append one or more entries to the account's deliverable change log (targeted field write, so a
 * concurrent full-document save cannot lose the entry as long as it runs after the main write).
 * @returns {Promise<Array>} the updated deliverableChangeLog array
 */
export const appendDeliverableChangeLog = async (accountId, entryOrEntries) => {
  requireAuth();
  const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
  const snap = await getDoc(accountRef);
  if (!snap.exists()) throw new Error('Account not found');
  const log = appendToLog(snap.data().deliverableChangeLog, entryOrEntries);
  await updateDoc(accountRef, {
    deliverableChangeLog: log,
    updatedAt: Timestamp.fromDate(new Date()),
  });
  return log;
};

/**
 * Stage a deliverable change for the next period. One pending change per item: scheduling a new
 * change for an item replaces its existing pending entry (the replacement is logged).
 * @returns {Promise<{scheduledDeliverableChanges: Array, deliverableChangeLog: Array}>}
 */
export const scheduleDeliverableChange = async (
  accountId,
  { type, itemId, itemName, quantity, previousQuantity, effectiveDate, effectivePeriodKey, actor }
) => {
  requireAuth();
  if (!itemId || !type) throw new Error('itemId and type are required');
  if (!effectiveDate) throw new Error('effectiveDate is required');
  const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
  const snap = await getDoc(accountRef);
  if (!snap.exists()) throw new Error('Account not found');
  const data = snap.data();

  const entry = {
    id: makeEntryId(),
    type,
    itemId,
    itemName: itemName || itemId,
    quantity: Number.isFinite(Number(quantity)) ? Number(quantity) : null,
    previousQuantity: Number.isFinite(Number(previousQuantity)) ? Number(previousQuantity) : null,
    effectiveDate,
    effectivePeriodKey: effectivePeriodKey || null,
    createdAt: new Date().toISOString(),
    createdBy: actor || 'Unknown User',
  };

  const existing = Array.isArray(data.scheduledDeliverableChanges)
    ? data.scheduledDeliverableChanges
    : [];
  const replaced = existing.find((c) => c && c.itemId === itemId);
  const pending = [...existing.filter((c) => !(c && c.itemId === itemId)), entry];

  const logEntries = [];
  if (replaced) {
    logEntries.push(
      buildChangeLogEntry({
        actor: actor || 'Unknown User',
        action: 'schedule-cancelled',
        itemId,
        itemName: entry.itemName,
        description: `Replaced scheduled change: ${describeScheduledChange(replaced)}`,
      })
    );
  }
  logEntries.push(
    buildChangeLogEntry({
      actor: actor || 'Unknown User',
      action: 'scheduled',
      itemId,
      itemName: entry.itemName,
      description: `Scheduled for next period: ${describeScheduledChange(entry)}`,
      effectiveDate,
    })
  );

  const log = appendToLog(data.deliverableChangeLog, logEntries);
  await updateDoc(accountRef, {
    scheduledDeliverableChanges: pending,
    deliverableChangeLog: log,
    updatedAt: Timestamp.fromDate(new Date()),
  });
  return { scheduledDeliverableChanges: pending, deliverableChangeLog: log };
};

/**
 * Cancel a pending scheduled change before it applies.
 * @returns {Promise<{scheduledDeliverableChanges: Array, deliverableChangeLog: Array}>}
 */
export const cancelScheduledDeliverableChange = async (accountId, changeId, actor) => {
  requireAuth();
  const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
  const snap = await getDoc(accountRef);
  if (!snap.exists()) throw new Error('Account not found');
  const data = snap.data();
  const existing = Array.isArray(data.scheduledDeliverableChanges)
    ? data.scheduledDeliverableChanges
    : [];
  const cancelled = existing.find((c) => c && c.id === changeId);
  const pending = existing.filter((c) => !(c && c.id === changeId));
  if (!cancelled) return { scheduledDeliverableChanges: pending, deliverableChangeLog: data.deliverableChangeLog || [] };

  const log = appendToLog(
    data.deliverableChangeLog,
    buildChangeLogEntry({
      actor: actor || 'Unknown User',
      action: 'schedule-cancelled',
      itemId: cancelled.itemId,
      itemName: cancelled.itemName,
      description: `Cancelled scheduled change: ${describeScheduledChange(cancelled)}`,
    })
  );
  await updateDoc(accountRef, {
    scheduledDeliverableChanges: pending,
    deliverableChangeLog: log,
    updatedAt: Timestamp.fromDate(new Date()),
  });
  return { scheduledDeliverableChanges: pending, deliverableChangeLog: log };
};

/**
 * Fold ONE scheduled change into the account's roster fields. Pure — mirrors the Program tab's
 * immediate handlers (handleAddDeliverable / handleRemoveDeliverable /
 * handleAdjustDeliverableQuantity in ClientOverview) so a scheduled change lands exactly where an
 * immediate one would have.
 *
 * @param {object} data account fields (program + the 5 roster fields)
 * @param {object} change scheduled change entry
 * @returns {object} the 5 updated roster fields
 */
export function applyScheduledChangeToAccountFields(data, change) {
  const status = typeof data.program === 'string' ? data.program : data.program?.status;
  const isCustom = status === 'Custom';
  const itemId = change.itemId;

  const customList = Array.isArray(data.customProgramDeliverables)
    ? [...data.customProgramDeliverables]
    : [];
  const customQty = { ...(data.customProgramDeliverableQuantities || {}) };
  let extras = Array.isArray(data.extraDeliverables) ? [...data.extraDeliverables] : [];
  let removedIds = Array.isArray(data.removedDeliverableIds) ? [...data.removedDeliverableIds] : [];
  const overrides = { ...(data.deliverableQuantityOverrides || {}) };

  const qty = Math.max(0, parseInt(String(change.quantity), 10) || 0);

  if (change.type === 'add') {
    const addQty = Math.max(1, qty || 1);
    const wasRemovedTemplateItem = removedIds.includes(itemId);
    removedIds = removedIds.filter((id) => id !== itemId);
    if (isCustom) {
      if (!customList.includes(itemId)) customList.push(itemId);
      customQty[itemId] = addQty;
      delete overrides[itemId];
    } else if (wasRemovedTemplateItem) {
      // Restoring a removed template row: un-removing brings the row back; the override sets its
      // quantity. Pushing to extraDeliverables here would duplicate the row.
      overrides[itemId] = addQty;
    } else if (extras.some((e) => e && e.id === itemId)) {
      extras = extras.map((e) => (e && e.id === itemId ? { ...e, quantity: addQty } : e));
    } else {
      extras.push({ id: itemId, quantity: addQty });
    }
  } else if (change.type === 'remove') {
    if (isCustom && customList.includes(itemId)) {
      const idx = customList.indexOf(itemId);
      customList.splice(idx, 1);
      delete customQty[itemId];
    } else if (extras.some((e) => e && e.id === itemId)) {
      extras = extras.filter((e) => !(e && e.id === itemId));
    } else if (!removedIds.includes(itemId)) {
      removedIds.push(itemId);
    }
    delete overrides[itemId];
  } else if (change.type === 'quantity') {
    if (customList.includes(itemId)) {
      customQty[itemId] = qty;
      delete overrides[itemId];
    } else if (extras.some((e) => e && e.id === itemId)) {
      extras = extras.map((e) => (e && e.id === itemId ? { ...e, quantity: qty } : e));
    } else {
      overrides[itemId] = qty;
      delete customQty[itemId];
    }
  }

  return {
    customProgramDeliverables: customList,
    customProgramDeliverableQuantities: customQty,
    extraDeliverables: extras,
    removedDeliverableIds: removedIds,
    deliverableQuantityOverrides: overrides,
  };
}

/**
 * Apply every scheduled change whose effectiveDate has arrived. Re-reads the account doc so a
 * stale in-memory copy can't clobber concurrent edits, writes the updated roster + remaining
 * pending list + log entries, and returns the updated account object ({id, ...fields}) — or null
 * when nothing was due (the overwhelmingly common case).
 */
export async function applyDueScheduledDeliverableChanges(account) {
  const accountId = account?.id;
  if (!accountId) return null;
  const auth = getAuth();
  if (!auth.currentUser) return null;

  const now = new Date();
  const isDue = (c) => {
    const d = parseDateish(c?.effectiveDate);
    return !!d && d.getTime() <= now.getTime();
  };

  // Cheap pre-check on the in-memory copy before paying for a fresh read.
  const inMemoryPending = Array.isArray(account.scheduledDeliverableChanges)
    ? account.scheduledDeliverableChanges
    : [];
  if (!inMemoryPending.some(isDue)) return null;

  const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
  const snap = await getDoc(accountRef);
  if (!snap.exists()) return null;
  const data = snap.data();

  const allPending = Array.isArray(data.scheduledDeliverableChanges)
    ? data.scheduledDeliverableChanges
    : [];
  const due = allPending.filter(isDue);
  const remaining = allPending.filter((c) => !isDue(c));
  if (due.length === 0) return null;

  due.sort((a, b) => String(a?.createdAt || '').localeCompare(String(b?.createdAt || '')));

  let fields = {
    program: data.program,
    customProgramDeliverables: data.customProgramDeliverables,
    customProgramDeliverableQuantities: data.customProgramDeliverableQuantities,
    extraDeliverables: data.extraDeliverables,
    removedDeliverableIds: data.removedDeliverableIds,
    deliverableQuantityOverrides: data.deliverableQuantityOverrides,
  };
  due.forEach((change) => {
    fields = { program: data.program, ...applyScheduledChangeToAccountFields(fields, change) };
  });

  const logEntries = due.map((change) =>
    buildChangeLogEntry({
      actor: change.createdBy ? `Scheduled by ${change.createdBy}` : 'Scheduled change',
      action: 'applied',
      itemId: change.itemId,
      itemName: change.itemName,
      description: `Applied on reset date: ${describeScheduledChange(change)}`,
      effectiveDate: change.effectiveDate,
    })
  );
  const log = appendToLog(data.deliverableChangeLog, logEntries);

  const update = {
    customProgramDeliverables: fields.customProgramDeliverables,
    customProgramDeliverableQuantities: fields.customProgramDeliverableQuantities,
    extraDeliverables: fields.extraDeliverables,
    removedDeliverableIds: fields.removedDeliverableIds,
    deliverableQuantityOverrides: fields.deliverableQuantityOverrides,
    scheduledDeliverableChanges: remaining,
    deliverableChangeLog: log,
    updatedAt: Timestamp.fromDate(new Date()),
  };
  await updateDoc(accountRef, update);
  return { id: accountId, ...data, ...update };
}
